import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_SORT_KEY,
  REMINDERS_FILTER_STORAGE_KEY,
} from '../utils/filterConstants';
import { applyDatePreset } from '../utils/formatters';

const PAGE_SIZE = 10;

function readStoredFilters() {
  try {
    const saved = sessionStorage.getItem(REMINDERS_FILTER_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    // ignore
  }
  return {};
}

export function useReminderFilters() {
  const [searchParams] = useSearchParams();
  const contactId = searchParams.get('contact_id') || '';
  const [initial] = useState(readStoredFilters);

  const [page, setPage] = useState(initial.page || 1);
  const [search, setSearch] = useState(initial.search || '');
  const [debouncedSearch, setDebouncedSearch] = useState(
    initial.debouncedSearch || initial.search || '',
  );

  const [typeFilter, setTypeFilter] = useState(initial.typeFilter || '');
  const [statusFilter, setStatusFilter] = useState(initial.statusFilter || '');
  const [assignedEmployeeId, setAssignedEmployeeId] = useState(
    initial.assignedEmployeeId || '',
  );
  const [dateFrom, setDateFrom] = useState(initial.dateFrom || '');
  const [dateTo, setDateTo] = useState(initial.dateTo || '');
  const [sortKey, setSortKey] = useState(initial.sortKey || DEFAULT_SORT_KEY);
  const [activeFilterFields, setActiveFilterFields] = useState(
    initial.activeFilterFields || [],
  );

  const isInitialSearchMount = useRef(true);

  useEffect(() => {
    if (isInitialSearchMount.current) {
      isInitialSearchMount.current = false;
      return;
    }
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    sessionStorage.setItem(
      REMINDERS_FILTER_STORAGE_KEY,
      JSON.stringify({
        search,
        debouncedSearch,
        typeFilter,
        statusFilter,
        assignedEmployeeId,
        dateFrom,
        dateTo,
        sortKey,
        activeFilterFields,
        page,
      }),
    );
  }, [
    search,
    debouncedSearch,
    typeFilter,
    statusFilter,
    assignedEmployeeId,
    dateFrom,
    dateTo,
    sortKey,
    activeFilterFields,
    page,
  ]);

  const resetPage = () => setPage(1);

  const filters = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      search: debouncedSearch,
      typeFilter,
      statusFilter,
      fromDate: dateFrom,
      toDate: dateTo,
      contactId,
      assignedEmployeeId,
      sortKey: activeFilterFields.includes('sort') ? sortKey : '',
    }),
    [
      page,
      debouncedSearch,
      typeFilter,
      statusFilter,
      dateFrom,
      dateTo,
      contactId,
      assignedEmployeeId,
      sortKey,
      activeFilterFields,
    ],
  );

  const hasActiveFilters =
    activeFilterFields.length > 0 || Boolean(debouncedSearch);

  const handleAddFilterField = (key) => {
    setActiveFilterFields((prev) =>
      prev.includes(key) ? prev : [...prev, key],
    );
  };

  const handleRemoveFilterField = (key) => {
    setActiveFilterFields((prev) => prev.filter((f) => f !== key));
    resetPage();
    if (key === 'type') setTypeFilter('');
    if (key === 'status') setStatusFilter('');
    if (key === 'assigned') setAssignedEmployeeId('');
    if (key === 'dateRange') {
      setDateFrom('');
      setDateTo('');
    }
    if (key === 'sort') setSortKey(DEFAULT_SORT_KEY);
  };

  const clearAllFilters = () => {
    resetPage();
    setSearch('');
    setDebouncedSearch('');
    setTypeFilter('');
    setStatusFilter('');
    setAssignedEmployeeId('');
    setDateFrom('');
    setDateTo('');
    setSortKey(DEFAULT_SORT_KEY);
    setActiveFilterFields([]);
  };

  const handleSummaryClick = (cardFilter) => {
    resetPage();
    if (cardFilter.preset) {
      const preset = applyDatePreset(cardFilter.preset);
      setDateFrom(preset.fromDate ? preset.fromDate.slice(0, 10) : '');
      setDateTo(preset.toDate ? preset.toDate.slice(0, 10) : '');
      setActiveFilterFields((prev) => {
        const next = prev.filter((f) => f !== 'dateRange' && f !== 'status');
        return [...next, 'status'];
      });
      setStatusFilter(cardFilter.preset);
      return;
    }
    if (cardFilter.status?.length === 1) {
      setDateFrom('');
      setDateTo('');
      setStatusFilter(cardFilter.status[0]);
      setActiveFilterFields((prev) => {
        const next = prev.filter((f) => f !== 'dateRange');
        return next.includes('status') ? next : [...next, 'status'];
      });
    }
  };

  return {
    page,
    setPage,
    pageSize: PAGE_SIZE,
    search,
    setSearch: (value) => {
      resetPage();
      setSearch(value);
    },
    typeFilter,
    setTypeFilter: (value) => {
      resetPage();
      setTypeFilter(value);
    },
    statusFilter,
    setStatusFilter: (value) => {
      resetPage();
      setStatusFilter(value);
    },
    assignedEmployeeId,
    setAssignedEmployeeId: (value) => {
      resetPage();
      setAssignedEmployeeId(value);
    },
    dateFrom,
    dateTo,
    setDateRange: (start, end) => {
      resetPage();
      setDateFrom(start || '');
      setDateTo(end || '');
    },
    sortKey,
    setSortKey: (value) => {
      resetPage();
      setSortKey(value);
    },
    activeFilterFields,
    contactId,
    filters,
    hasActiveFilters,
    handleAddFilterField,
    handleRemoveFilterField,
    clearAllFilters,
    handleSummaryClick,
  };
}
